import React, { Component } from 'react';
import DtnService from '../../../../services/DtnService';

export default class StatusButtons extends Component {
    constructor(props) {
        super(props);

        this.state = {
            loadingCompleted: false,

            showReasonModal: false,
            showManagerReviewModal: false,
            showSuperadminReviewModal: false,

            reason: '',
            managerReviewReason: '',
            superadminReviewReason: '',

            pendingStatus: null,
            requiresDtnReasonForReview: false,
            requiresDtnReasonForSuperadmin: false,
            hasPendingSuperadminReview: false,
        };

        this.dtnService = new DtnService();
    }

    async componentDidMount() {
        await this.checkPendingSuperadminReview();
    }

    async componentDidUpdate(prevProps) {
        if (prevProps.jobId !== this.props.jobId) {
            await this.checkPendingSuperadminReview();
        }
    }

    checkPendingSuperadminReview = async () => {
        try {
            const superadminRes = await this.dtnService.requiresSuperadminReview(this.props.jobId);

            this.setState({
                hasPendingSuperadminReview: !!superadminRes.data.hasPending,
            });
        } catch (err) {
            console.error('Error checking superadmin review status:', err);
        }
    };

    changeStatus = async (statusObj) => {
        try {
            this.props.disableStatusButtons?.();

            if (this.props.saveDtn) {
                const dtnSaved = await this.props.saveDtn(false);
                if (!dtnSaved) {
                    toastr.error('Please fix validation errors and save the DTN before updating the job status.');
                    this.props.enableStatusButtons?.();
                    return;
                }
            }

            if (statusObj.waste_journey) {
                this.submitStatus(statusObj);
                return;
            }

            const roleName = this.props.user.role?.name;
            const isAdminOrManager = ['admin', 'manager'].includes(roleName);
            const isSuperadmin = ['superadmin', 'admin'].includes(roleName);

            // Superadmin review comes first if there is already a pending one
            if (['completed', 'done'].includes(statusObj.status) && isSuperadmin) {
                const superadminRes = await this.dtnService.requiresSuperadminReview(this.props.jobId);

                if (superadminRes.data.hasPending) {
                    this.setState({
                        showSuperadminReviewModal: true,
                        pendingStatus: statusObj,
                        requiresDtnReasonForSuperadmin: !!superadminRes.data.requiresWtnReason,
                        hasPendingSuperadminReview: true,
                    });
                    return;
                }
            }

            // Manager review path
            if (statusObj.status === 'completed' && isAdminOrManager && this.props.job.manager_review) {
                const reasonRes = await this.dtnService.requiresDtnReason(this.props.jobId);

                this.setState({
                    showManagerReviewModal: true,
                    pendingStatus: statusObj,
                    requiresDtnReasonForReview: !!reasonRes.data.requiresReason,
                });

                return;
            }

            // Direct DTN reason path only when there is no manager review
            const reasonRes = await this.dtnService.requiresDtnReason(this.props.jobId);
            if (reasonRes.data.requiresReason && !statusObj.reason) {
                this.setState({
                    showReasonModal: true,
                    pendingStatus: statusObj,
                    requiresDtnReasonForReview: false,
                    requiresDtnReasonForSuperadmin: false,
                });
                return;
            }

            this.submitStatus(statusObj);
        } catch (err) {
            console.error('Error changing status:', err);
            toastr.error('An error occurred while trying to change the job status.');
            this.props.enableStatusButtons?.();
        }
    };

    submitStatusWithReason = () => {
        const { pendingStatus, reason } = this.state;

        if (!reason.trim()) {
            toastr.error('Please enter a DTN reason.');
            return;
        }

        const payload = {
            ...pendingStatus,
            reason: reason.trim(),
        };

        this.setState(
            {
                reason: '',
                showReasonModal: false,
                pendingStatus: payload,
            },
            () => {
                this.changeStatus(payload);
            }
        );
    };

    submitManagerReview = () => {
        const { pendingStatus, managerReviewReason, reason, requiresDtnReasonForReview } = this.state;

        if (!managerReviewReason?.trim()) {
            toastr.error('Please enter a manager review reason.');
            return;
        }

        if (requiresDtnReasonForReview && !reason?.trim()) {
            toastr.error('Please enter a DTN reason.');
            return;
        }

        const payload = {
            ...pendingStatus,
            manager_review_reason: managerReviewReason.trim(),
            manager_review_approve: 1,
        };

        if (requiresDtnReasonForReview) {
            payload.reason = reason.trim();
        }

        this.submitStatus(payload, { source: 'manager_review' });
    };

    submitSuperadminReview = () => {
        const { pendingStatus, superadminReviewReason, reason, requiresDtnReasonForSuperadmin } = this.state;

        if (!superadminReviewReason?.trim()) {
            toastr.error('Please enter a superadmin review reason.');
            return;
        }

        if (requiresDtnReasonForSuperadmin && !reason?.trim()) {
            toastr.error('Please enter a DTN reason.');
            return;
        }

        const payload = {
            ...pendingStatus,
            superadmin_review_reason: superadminReviewReason.trim(),
            superadmin_review_approve: 1,
        };

        if (requiresDtnReasonForSuperadmin) {
            payload.reason = reason.trim();
        }

        this.submitStatus(payload, { source: 'superadmin_review' });
    };

    submitStatus = (statusObj, options = {}) => {
        const { source = null } = options;

        this.props.disableStatusButtons?.();

        const formData = new FormData();
        formData.append('_method', 'PATCH');
        formData.append('status', statusObj.status);

        if (statusObj.reason) {
            formData.append('reason', statusObj.reason);
        }

        if (statusObj.waste_journey !== undefined) {
            formData.append('waste_journey', statusObj.waste_journey);
        }

        if (statusObj.manager_review_reason) {
            formData.append('manager_review_reason', statusObj.manager_review_reason);
        }

        if (statusObj.manager_review_approve) {
            formData.append('manager_review_approve', statusObj.manager_review_approve);
        }

        if (statusObj.superadmin_review_reason !== undefined) {
            formData.append('superadmin_review_reason', statusObj.superadmin_review_reason);
        }

        if (statusObj.superadmin_review_approve) {
            formData.append('superadmin_review_approve', statusObj.superadmin_review_approve);
        }

        this.setState({ loadingCompleted: true });

        this.dtnService
            .updateJobStatus(this.props.jobId, formData)
            .then((res) => {
                const needsSuperadminReview = !!res.data?.needs_superadmin_review;

                if (source === 'manager_review' && needsSuperadminReview) {
                    this.dtnService.requiresSuperadminReview(this.props.jobId).then((superadminRes) => {

                        this.setState({
                            loadingCompleted: false,
                            showReasonModal: false,
                            showManagerReviewModal: false,
                            showSuperadminReviewModal: true,
                            managerReviewReason: '',
                            superadminReviewReason: '',
                            reason: '',
                            requiresDtnReasonForReview: false,
                            requiresDtnReasonForSuperadmin: !!superadminRes.data.requiresWtnReason,
                            hasPendingSuperadminReview: true,
                            pendingStatus: {
                                status: 'completed',
                                waste_journey: statusObj.waste_journey ?? 0,
                            },
                        });

                        toastr.success(res.data?.message ?? 'Manager approved. Awaiting superadmin review.');
                    });

                    return;
                }

                this.setState({
                    loadingCompleted: false,
                    reason: '',
                    managerReviewReason: '',
                    superadminReviewReason: '',
                    pendingStatus: null,
                    requiresDtnReasonForReview: false,
                    requiresDtnReasonForSuperadmin: false,
                    hasPendingSuperadminReview: false,
                    showReasonModal: false,
                    showManagerReviewModal: false,
                    showSuperadminReviewModal: false,
                });

                toastr.success(res.data?.message ?? 'Job status successfully updated');
                this.props.enableStatusButtons?.();
                this.props.refreshJob?.();
                this.checkPendingSuperadminReview();
            })
            .catch((err) => {
                console.error('❌ Error updating job status:', err);

                this.setState({ loadingCompleted: false });
                this.props.enableStatusButtons?.();

                toastr.error(err.response?.data?.message ?? 'There was a problem, please try again later');
            });
    };

    render() {
        const {
            loadingCompleted,
            showReasonModal,
            showManagerReviewModal,
            showSuperadminReviewModal,
            reason,
            managerReviewReason,
            superadminReviewReason,
            requiresDtnReasonForReview,
            requiresDtnReasonForSuperadmin,
            hasPendingSuperadminReview,
        } = this.state;

        const roleName = this.props.user.role?.name;
        const isSuperadminUser = ['superadmin'].includes(roleName);

        const hideCompletedButton = hasPendingSuperadminReview && !isSuperadminUser;

        return (
            <>
                {!hideCompletedButton && (
                    <button
                        type="button"
                        className="btn btn-cw-green ml-3"
                        style={{ width: 200, marginTop: 20, marginLeft: 15 }}
                        onClick={() =>
                            this.changeStatus({
                                status: 'completed',
                                waste_journey: 0,
                                manager_review_flag_reason: this.props.job.manager_review_flag_reason,
                            })
                        }
                        disabled={this.props.disabled}
                        title={this.props.title}
                    >
                        Mark as completed &nbsp;
                        {loadingCompleted && <span className="spinner-border spinner-border-sm" role="status" />}
                    </button>
                )}

                {showReasonModal && (
                    <div className="modal show" style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Yardage Change Reason Required</h5>
                                </div>
                                <div className="modal-body">
                                    <textarea
                                        className="form-control"
                                        value={reason}
                                        onChange={(e) => this.setState({ reason: e.target.value })}
                                        placeholder="Enter Yardage Change reason"
                                    />
                                </div>
                                <div className="modal-footer">
                                    <button
                                        className="btn btn-secondary"
                                        onClick={() =>
                                            this.setState({
                                                showReasonModal: false,
                                                reason: '',
                                                pendingStatus: null,
                                                requiresDtnReasonForReview: false,
                                                requiresDtnReasonForSuperadmin: false,
                                            })
                                        }
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="btn btn-primary"
                                        disabled={!reason.trim()}
                                        onClick={this.submitStatusWithReason}
                                    >
                                        Submit
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {showManagerReviewModal && (
                    <div className="modal show" style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Manager Review Required</h5>
                                </div>
                                <div className="modal-body">
                                    <p>This job requires a manager review.</p>

                                    <label className="mt-2 font-weight-bold">Manager review reason</label>
                                    <textarea
                                        className="form-control mt-1"
                                        value={managerReviewReason}
                                        onChange={(e) => this.setState({ managerReviewReason: e.target.value })}
                                        placeholder="Enter manager review reason"
                                    />

                                    {requiresDtnReasonForReview && (
                                        <>
                                            <label className="mt-3 font-weight-bold">Yardage Change reason</label>
                                            <textarea
                                                className="form-control mt-1"
                                                value={reason}
                                                onChange={(e) => this.setState({ reason: e.target.value })}
                                                placeholder="Enter Yardage Change reason"
                                            />
                                        </>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button
                                        className="btn btn-secondary"
                                        onClick={() =>
                                            this.setState({
                                                showManagerReviewModal: false,
                                                managerReviewReason: '',
                                                reason: '',
                                                pendingStatus: null,
                                                requiresDtnReasonForReview: false,
                                            })
                                        }
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="btn btn-primary"
                                        disabled={
                                            !managerReviewReason.trim() ||
                                            (requiresDtnReasonForReview && !reason.trim())
                                        }
                                        onClick={this.submitManagerReview}
                                    >
                                        Confirm
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {showSuperadminReviewModal && (
                    <div className="modal show" style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Superadmin Review Required</h5>
                                </div>
                                <div className="modal-body">
                                    <p>This job requires superadmin approval.</p>

                                    <label className="mt-2 font-weight-bold">Superadmin review reason</label>
                                    <textarea
                                        className="form-control mt-1"
                                        value={superadminReviewReason}
                                        onChange={(e) => this.setState({ superadminReviewReason: e.target.value })}
                                        placeholder="Enter superadmin review reason"
                                    />

                                    {requiresDtnReasonForSuperadmin && (
                                        <>
                                            <label className="mt-3 font-weight-bold">Yardage Change reason</label>
                                            <textarea
                                                className="form-control mt-1"
                                                value={reason}
                                                onChange={(e) => this.setState({ reason: e.target.value })}
                                                placeholder="Enter Yardage Change reason"
                                            />
                                        </>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button
                                        className="btn btn-secondary"
                                        onClick={() =>
                                            this.setState({
                                                showSuperadminReviewModal: false,
                                                superadminReviewReason: '',
                                                reason: '',
                                                pendingStatus: null,
                                                requiresDtnReasonForSuperadmin: false,
                                            })
                                        }
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="btn btn-primary"
                                        disabled={
                                            !superadminReviewReason.trim() ||
                                            (requiresDtnReasonForSuperadmin && !reason.trim())
                                        }
                                        onClick={this.submitSuperadminReview}
                                    >
                                        Confirm
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </>
        );
    }
}
